import { niceNumber } from "./Util";
import type { IGetLatestUsageKpiReportRes, IUsageKpiTimeRangeUsage } from "./backend/reports.generated.types";

export const USAGE_KPI_TIME_RANGE_KEYS = ["today", "yesterday", "-7days", "-30days"] as const;

export type TimeRangeKey = (typeof USAGE_KPI_TIME_RANGE_KEYS)[number];

/** One value for a metric in a single time window. */
export type TimeRange = {
    timeRangeKey: TimeRangeKey;
    value: string;
};

/** Org- or plant-level usage grouped by metric; each metric is an ordered array of points. */
export type UsageByMetric = {
    visits: TimeRange[];
    visitors: TimeRange[];
};

export type PlantUsageViewModel = {
    uuid: string;
    title: string;
    external_id: string;
    kpis: UsageByMetric;
};

export type OrgUsageViewModel = {
    uuid: string | null;
    code: string | null;
    title: string | null;
    kpis: UsageByMetric;
};

export type ViewModel = {
    createdAt: string | null;
    timeRangeKeys: TimeRangeKey[];
    org: OrgUsageViewModel;
    plants: PlantUsageViewModel[];
};

/** Formats a usage count for display, or "-" when the value is missing. */
const formatNumber = (value: number | undefined): string => {
    return value !== undefined ? niceNumber(value) : "-";
};

/** Builds ordered points for one metric (visits or visitors) across the given time windows. */
const mapMetricPoints = (
    timeRanges: Partial<Record<TimeRangeKey, IUsageKpiTimeRangeUsage>>,
    keys: TimeRangeKey[],
    metric: keyof UsageByMetric
): TimeRange[] => {
    return keys.map(timeRangeKey => ({
        timeRangeKey,
        value: formatNumber(timeRanges[timeRangeKey]?.usage?.[metric]),
    }));
};

/** Transforms API time ranges and maps visits and visitors per window to array points. */
const mapUsageByMetric = (
    timeRanges: Partial<Record<TimeRangeKey, IUsageKpiTimeRangeUsage>>,
    keys: TimeRangeKey[]
): UsageByMetric => {
    return {
        visits: mapMetricPoints(timeRanges, keys, "visits"),
        visitors: mapMetricPoints(timeRanges, keys, "visitors"),
    };
};

/**
 * Maps a usage KPI report to a view model for the Usage KPIs page.
 * Organizational data is mapped to a single view model object,
 * while plant data is mapped to an array of view model objects.
 * Metrics are grouped by visits / visitors, each as an array of { timeRangeKey, value }.
 */
export const mapReportToViewModel = (report: IGetLatestUsageKpiReportRes): ViewModel => {
    const timeRangeKeys = report.metadata.time_range_keys as TimeRangeKey[];

    return {
        createdAt: report.metadata.created_at ?? null,
        timeRangeKeys,
        org: {
            uuid: report.metadata.org_uuid ?? null,
            code: report.metadata.org_code ?? null,
            title: report.metadata.org_title ?? null,
            kpis: mapUsageByMetric(report.org?.time_ranges, timeRangeKeys),
        },
        plants: (report.plants ?? []).map(plant => {
            const { uuid, title, external_id } = plant;
            const kpis = mapUsageByMetric(plant.time_ranges, timeRangeKeys);

            return {
                uuid,
                title,
                external_id,
                kpis,
            };
        }),
    };
};
